import { ServiceLevel, VolumeFormat } from "api/rest/quote-requests";
import { ShipmentPackagingType } from "app/pages/shipments/types/domain";
import i18next from "i18next";
import { IncotermsEnum } from "types/Incoterms";
import { RoutingTypeEnum } from "types/RoutingType";
import { z } from "zod";

const addressSchema = z.object({
  id: z.number().optional(),
  name: z.string().nullable().optional(),
  address_1: z.string().nullable().optional(),
  address_2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
  country_code: z.string().nullable().optional(),
  country_name: z.string().nullable().optional(),
  province_code: z.string().nullable().optional(),
  province_name: z.string().nullable().optional(),
  google_place_id: z.string().nullable().optional(),
  google_place_description: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
});

const stopSchema = z.object({
  address: addressSchema.refine((val) => val != null, {
    message: i18next.t("quoteRequest:LocationStep.addressRequired"),
  }),
});

const nullableStopSchema = z.object({
  address: addressSchema.nullable(),
});

const baseRoutingStepSchema = z.object({
  incoterms: z.nativeEnum(IncotermsEnum),
  cargo_ready_date: z.string(),
  target_delivery_date: z.string().optional(),
});

const p2pRoutingStepSchema = z.object({
  routing_type: z.literal(RoutingTypeEnum.P2P),
  originStop: nullableStopSchema.optional(),
  originPort: stopSchema,
  destinationPort: stopSchema,
  destinationStop: nullableStopSchema.optional(),
});

const d2dRoutingStepSchema = z.object({
  routing_type: z.literal(RoutingTypeEnum.D2D),
  originStop: stopSchema,
  originPort: nullableStopSchema.optional(),
  destinationPort: nullableStopSchema.optional(),
  destinationStop: stopSchema,
});

const p2dRoutingStepSchema = z.object({
  routing_type: z.literal(RoutingTypeEnum.P2D),
  originStop: nullableStopSchema.optional(),
  originPort: stopSchema,
  destinationPort: nullableStopSchema.optional(),
  destinationStop: stopSchema,
});

const d2pRoutingStepSchema = z.object({
  routing_type: z.literal(RoutingTypeEnum.D2P),
  originStop: stopSchema,
  originPort: nullableStopSchema.optional(),
  destinationPort: stopSchema,
  destinationStop: nullableStopSchema.optional(),
});

export const routingStepSchema = z
  .discriminatedUnion(
    "routing_type",
    [p2pRoutingStepSchema, d2dRoutingStepSchema, p2dRoutingStepSchema, d2pRoutingStepSchema],
    {
      errorMap: (issue) => {
        if (issue.code === "invalid_union_discriminator") {
          return { message: "A routing type is required" };
        }
        return { message: issue.message ?? "" };
      },
    }
  )
  .and(baseRoutingStepSchema);

export type StopSchema = z.infer<typeof stopSchema>;
export type RoutingSchema = z.infer<typeof routingStepSchema>;

export const serviceSchema = routingStepSchema.and(
  z.object({
    serviceLevels: z.nativeEnum(ServiceLevel).array().min(1, { message: "At least one service level is required" }),
    carrierRoutingPref: z.string().optional(),
  })
);

export type ServiceSchema = z.infer<typeof serviceSchema>;

const airPackageGroupSchema = z
  .discriminatedUnion("volume_format", [
    z.object({
      volume_format: z.literal(VolumeFormat.Dimensions),
      length_per_package: z.number(),
      height_per_package: z.number(),
      width_per_package: z.number(),
    }),
    z.object({ volume_format: z.literal(VolumeFormat.Volume), volume_per_item: z.number() }),
  ])
  .and(
    z.object({
      packaging_type: z.nativeEnum(ShipmentPackagingType),
      item_quantity: z.number(),
      weight_per_package: z.number(),
    })
  );

export const cargoDetailsSchema = serviceSchema.and(
  z.object({
    airPackageGroups: z.array(airPackageGroupSchema).min(1, { message: "At least one package group is required" }),
  })
);

export type AirPackageGroupSchema = z.infer<typeof airPackageGroupSchema>;
export type CargoDetailsSchema = z.infer<typeof cargoDetailsSchema>;

export const shipmentDetailsSchema = cargoDetailsSchema.and(
  z
    .discriminatedUnion("isHazardous", [
      z.object({ isHazardous: z.literal(true), hazardousDetails: z.string().min(1) }),
      z.object({ isHazardous: z.literal(false), hazardousDetails: z.string().optional() }),
    ])
    .and(
      z
        .discriminatedUnion("includeCustomsClearance", [
          z.object({
            includeCustomsClearance: z.literal(true),
            commodities: z.string().min(1),
          }),
          z.object({
            includeCustomsClearance: z.literal(false),
            commodities: z.string().optional(),
          }),
        ])
        .and(
          z.object({
            poReferenceNumber: z.string().optional(),
            internalReferenceNumber: z.string().optional().nullable(),
            deadlineRespondAt: z.string().optional(),
            includeCargoInsurance: z.boolean(),
            additionalNotes: z.string().optional(),
          })
        )
    )
);

export type ShipmentDetailsSchema = z.infer<typeof shipmentDetailsSchema>;

export const partnersSchema = shipmentDetailsSchema.and(
  z.object({
    partners: z.object({ contact_id: z.number() }).array().min(1, { message: "At least one partner must be selected" }),
  })
);

export type PartnersScheam = z.infer<typeof partnersSchema>;

export const teamSchema = partnersSchema.and(
  z.object({
    teammates: z.object({ contact_id: z.number() }).array().optional(),
  })
);

export type TeamSchema = z.infer<typeof teamSchema>;

export type FinalSchema = TeamSchema;
